import CSSpan from "@components/HTMLElements/CSSpan"
import SectionGroup from "@components/SectionGroup/SectionGroup"
import Localization from "@localization/Index"
import { formatDurationFromMilliseconds } from "@utils/DateUtils"
import { copyToClipboard } from "@utils/StringUtils"
import { getTransactionTypeTheme } from "@utils/TrackingUtils"
import { format, parseISO } from "date-fns"
import { useState } from "react"
import { Badge, Tab, Tabs } from "react-bootstrap"
import { JSONTree } from "react-json-tree"
import { ToastContainer } from "react-toastify"
import { TransactionSelected } from "./Tracking"

type Props = {
    transaction: TransactionSelected
    totalTransactions: number
    onClose: ()=>unknown
    onChangeIndex: (index: number)=>unknown
}

const TransactionDetails = (props: Props) =>{
    const { transaction, totalTransactions, onClose, onChangeIndex } = props
    const { index, _id, msecs, t, ctx, req, res } = transaction
    const [currentTab, setCurrentTab] = useState<string>("0")
    const [currentRequestTab, setCurrentRequestTab] = useState<string>("0")
    const [currentResponseTab, setCurrentResponseTab] = useState<string>("0")
    const { a } = ctx ?? {}

    const typeTheme = getTransactionTypeTheme(t)
    const attributes = Object.keys(a ?? {})

    const requestDate = (req?.date?.$date)?format(parseISO(req.date.$date), 'dd/MM/yyyy HH:mm:ss:SSS'):"N.D."
    const responseDate = (res?.date?.$date)?format(parseISO(res.date.$date), 'dd/MM/yyyy HH:mm:ss:SSS'):"N.D."

    const handleCopy = (text?: string) => {
        if(!text) return false

        return copyToClipboard({ text })
    }

    return <>
        <div className="w-100 d-flex align-items-center justify-content-between fs-3">
            <div className="d-flex align-items-center justify-content-start fs-3">
                <i className={`bi-chevron-left me-4 ${index<=0?"text-muted":""}`} role={index<=0?"":"button"} onClick={()=>{
                    if(index>0) { 
                        setCurrentTab("0")
                        setCurrentRequestTab("0")
                        setCurrentResponseTab("0")
                        return onChangeIndex(index-1) 
                    }
                }} />
                <span>{`${index+1} / ${totalTransactions}`}</span>
                <i className={`bi-chevron-right ms-4 ${index>=(totalTransactions-1)?"text-muted":""}`} role={index>=(totalTransactions-1)?"":"button"} onClick={()=>{
                    if(index<totalTransactions) { 
                        setCurrentTab("0")
                        setCurrentRequestTab("0")
                        setCurrentResponseTab("0")
                        return onChangeIndex(index+1) 
                    }
                }} />
            </div>
            <i className="bi-x font-2x" role="button" onClick={()=>{
                setCurrentTab("0");
                setCurrentRequestTab("0")
                setCurrentResponseTab("0")
                return onClose()
            }} />
        </div>
        <Tabs
            id="transaction-details-tabs"
            activeKey={currentTab}
            onSelect={(k) => setCurrentTab(k ?? "0")}
            className="cs-tabs mt-3"
        >
            <Tab eventKey="0" title={Localization.TRACKING.DETAILS.DATI_GENERALI} mountOnEnter>
                
                <div className="row">
                    <div className="col-md-12 d-flex flex-column flex-grow-1">
                        <SectionGroup small title={Localization.TRACKING.DETAILS.INFORMAZIONI} className="d-flex flex-column flex-grow-1">
                            <div className="mt-field">
                                <div className='row border-bottom py-4'>
                                    <div className="col-8 text-start">
                                        {Localization.TRACKING.TABLE.ID}
                                    </div>
                                    <div className="col-16 text-end">
                                        <Badge bg="primary">{_id}</Badge>
                                    </div>
                                </div>
                                <div className='row border-bottom py-4'>
                                    <div className="col-8 text-start">
                                        {Localization.TRACKING.TABLE.TIPO}
                                    </div>
                                    <div className="col-16 text-end">
                                        <Badge bg={typeTheme.variant}>{t}</Badge>
                                    </div>
                                </div>
                                <div className='row py-4'>
                                    <div className="col-8 text-start">
                                        {Localization.TRACKING.TABLE.TEMPO_DI_RISPOSTA}
                                    </div>
                                    <div className="col-16 text-end">
                                        {msecs && <>{formatDurationFromMilliseconds(msecs)}</>}
                                    </div>
                                </div>
                            </div>
                        </SectionGroup>

                    </div>
                    <div className="col-md-12 d-flex flex-column flex-grow-1">
                    <SectionGroup small title={Localization.TRACKING.DETAILS.ATTRIBUTI} className="d-flex flex-column flex-grow-1">
                        <div className="mt-field">
                            {attributes.map((attribute, index)=>{
                                const isLast = index === attributes.length -1

                                return <div key={`tracking_attributes_${attribute}_${index}`} className={`row py-4 ${isLast?"":"border-bottom"}`}>
                                    <div className="col-8 text-start text-break">
                                        {attribute}
                                    </div>
                                    <div className="col-16 text-end text-break">
                                        {a?.[attribute] as unknown as string}
                                    </div>
                                </div>
                            })}
                        </div>
                    </SectionGroup>
                    </div>
                </div>
            </Tab>
            <Tab eventKey="1" title={Localization.TRACKING.DETAILS.RICHIESTA} mountOnEnter>
                <Tabs
                    id="transaction-details-request-tabs"
                    activeKey={currentRequestTab}
                    onSelect={(k) => setCurrentRequestTab(k ?? "0")}
                    className="cs-tabs mt-3"
                >
                    <Tab eventKey="0" title="Payload" mountOnEnter>
                        <SectionGroup className='flex-grow-1' 
                            toolbar={<CSSpan role="button" tooltip={Localization.COPIA} onClick={()=>handleCopy(transaction.req?.p)}><i className="bi-copy font-2x text-muted" /></CSSpan>}> 
                            {transaction.req?.type === "JSON" && <JSONTree data={transaction.req?.p?JSON.parse(transaction.req?.p): {}} 
                                hideRoot
                                theme={{
                                    tree: ({ style }) => ({
                                        style: { ...style, backgroundColor: undefined, padding: '5px' }
                                    })
                                }}
                                shouldExpandNodeInitially={()=>true}
                                labelRenderer={([key]) => <span>{key}</span>}
                                valueRenderer={(raw) => <span>{raw as JSX.Element}</span>}
                            />}
                            {transaction.req?.type !== "JSON" && <pre className="p-3" style={{whiteSpace: 'pre-wrap', wordBreak: 'break-all'}}>{transaction.req?.p}</pre>}
                        </SectionGroup>
                    </Tab>
                    <Tab eventKey="1" title="Header">
                        <SectionGroup className='flex-grow-1'>
                            <div className="mt-field">
                                {transaction.req?.hdrs?.map((header, index)=>{
                                    const isLast = index === attributes.length -1

                                    return <div key={`tracking_request_header_${header.n}_${index}`} className={`row py-4 ${isLast?"":"border-bottom"}`}>
                                        <div className="col-8 fw-bold text-start text-break">
                                            {header.n}
                                        </div>
                                        <div className="col-16 text-start text-break">
                                            <CSSpan className="me-2" role="button" tooltip={Localization.COPIA} onClick={()=>handleCopy(header.v)}><i className="bi-copy text-muted" /></CSSpan>
                                            {header.v}
                                        </div>
                                    </div>
                                })}
                            </div>
                        </SectionGroup>
                    </Tab>
                    <Tab eventKey="2" title="Info">
                        <SectionGroup className='flex-grow-1'>
                            <div className="mt-field">
                                <div className='row border-bottom py-4'>
                                    <div className="col-8 fw-bold text-start">
                                        {Localization.TRACKING.DETAILS.INFO.DATA}
                                    </div>
                                    <div className="col-16 text-start">{requestDate}</div>
                                </div>
                                <div className='row border-bottom py-4'>
                                    <div className="col-8 fw-bold text-start">
                                        {Localization.TRACKING.DETAILS.INFO.URI}
                                    </div>
                                    <div className="col-16 text-start">
                                        <CSSpan className="me-2" role="button" tooltip={Localization.COPIA} onClick={()=>handleCopy(req?.uri)}><i className="bi-copy text-muted" /></CSSpan>
                                        {req?.uri}
                                    </div>
                                </div>
                                <div className='row border-bottom py-4'>
                                    <div className="col-8 fw-bold text-start">
                                        {Localization.TRACKING.DETAILS.INFO.METODO_HTTP}
                                    </div>
                                    <div className="col-16 text-start">{req?.method}</div>
                                </div>
                                <div className='row border-bottom py-4'>
                                    <div className="col-8 fw-bold text-start">
                                        {Localization.TRACKING.DETAILS.INFO.TIPO_PAYLOAD}
                                    </div>
                                    <div className="col-16 text-start">{req?.type}</div>
                                </div>
                            </div>
                        </SectionGroup>
                    </Tab>
                </Tabs>
            </Tab>
            <Tab eventKey="2" title={Localization.TRACKING.DETAILS.RISPOSTA} mountOnEnter>
                <Tabs
                    id="transaction-details-response-tabs"
                    activeKey={currentResponseTab}
                    onSelect={(k) => setCurrentResponseTab(k ?? "0")}
                    className="cs-tabs mt-3"
                >
                    <Tab eventKey="0" title="Payload">
                    <SectionGroup className='flex-grow-1' 
                            toolbar={<CSSpan role="button" tooltip={Localization.COPIA} onClick={()=>handleCopy(transaction.res?.p)}><i className="bi-copy font-2x text-muted" /></CSSpan>}> 
                            {transaction.res?.type === "JSON" && <JSONTree data={transaction.res?.p ? JSON.parse(transaction.res?.p):{}} 
                                hideRoot
                                theme={{
                                    tree: ({ style }) => ({
                                        style: { ...style, backgroundColor: undefined, padding: '5px' }
                                    })
                                }}
                                shouldExpandNodeInitially={()=>true}
                                labelRenderer={([key]) => <span>{key}</span>}
                                valueRenderer={(raw) => <span>{raw as JSX.Element}</span>}
                            />}
                            {transaction.res?.type !== "JSON" && <pre className="p-3" style={{whiteSpace: 'pre-wrap', wordBreak: 'break-all'}}>{transaction.res?.p}</pre>}
                        </SectionGroup>
                    </Tab>
                    <Tab eventKey="1" title="Header">
                        <SectionGroup className='flex-grow-1'>
                            <div className="mt-field">
                                {transaction.res?.hdrs?.map((header, index)=>{
                                    const isLast = index === attributes.length -1

                                    return <div key={`tracking_response_header_${header.n}_${index}`} className={`row py-4 ${isLast?"":"border-bottom"}`}>
                                        <div className="col-8 text-start fw-bold text-break">
                                            {header.n}
                                        </div>
                                        <div className="col-16 text-start text-break">
                                        <CSSpan className="me-2" role="button" tooltip={Localization.COPIA} onClick={()=>handleCopy(header.v)}><i className="bi-copy text-muted" /></CSSpan>
                                            {header.v}
                                        </div>
                                    </div>
                                })}
                            </div>
                        </SectionGroup>
                    </Tab>
                    <Tab eventKey="2" title="Info">
                        <SectionGroup className='flex-grow-1'>
                            <div className="mt-field">
                                <div className='row border-bottom py-4'>
                                    <div className="col-8 fw-bold text-start">
                                        {Localization.TRACKING.DETAILS.INFO.DATA}
                                    </div>
                                    <div className="col-16 text-start">{responseDate}</div>
                                </div>
                                <div className='row border-bottom py-4'>
                                    <div className="col-8 fw-bold text-start">
                                        {Localization.TRACKING.DETAILS.INFO.CODICE_HTTP}
                                    </div>
                                    <div className="col-16 text-start">
                                        {res?.http_code}
                                    </div>
                                </div>
                                <div className='row border-bottom py-4'>
                                    <div className="col-8 fw-bold text-start">
                                        {Localization.TRACKING.DETAILS.INFO.ERRORE}
                                    </div>
                                    <div className="col-16 text-start">{res?.app_err?Localization.SI:Localization.NO}</div>
                                </div>
                                <div className='row border-bottom py-4'>
                                    <div className="col-8 fw-bold text-start">
                                        {Localization.TRACKING.DETAILS.INFO.TIPO_PAYLOAD}
                                    </div>
                                    <div className="col-16 text-start">{res?.type}</div>
                                </div>
                            </div>
                        </SectionGroup>
                    </Tab>
                </Tabs>
            </Tab>
        </Tabs>
        <ToastContainer />
    </>
}

export default TransactionDetails